// Globals
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Progress } from 'antd';

// Components
import { AttentionIcon, DocumentIcon, ReplaceIcon } from '@/icons';
import {
  StyledLabel,
  StyledDescription,
  StyledInputWrap,
  StyledSelect,
  StyledSelectWrap,
  StyledRow,
  Circle,
  StyledButton,
  StyledContinueBlock,
  StyledImportForm,
  StyledFormItem,
  StyledUploadContent,
  StyledButtonBlocksWrap,
  StyledButtonsWrap,
  StyledStockWrapper,
} from '../Styled';
import {
  StyledDragger,
  StyledHintBlock,
  StyledHintIcon,
  StyledHintText,
  StyledHintTitle,
  StyledHintTitleWrapper,
  StyledInstructionBlock,
  StyledLink,
  StyledExportProgress,
} from './StocksImportStyles';
import { IFormInstance } from '@/components';

// Helpers
import { useImportProductsFormRules } from '../../hooks/useImportProductsFormRules';
import { ImportStocksFieldNames, TImportStocksForm } from '../../helpers/types';
import { IMPORT_STOCKS_INSTRUCTION_LINK } from '../../helpers/constants';

// Models
import { ITemplateExportProgress } from '@/models/productsImport.model';

type TPreparingStepContainer = {
  onSubmit: (values: TImportStocksForm) => void;
  onValuesChange: (values: TImportStocksForm) => void;
  categoryOptions: { value: string; label: string }[];
  partnersOptions: { value: string; label: string }[];
  onPartnerSearch: (name: string) => void;
  onCategoriesSearch: (name: string) => void;
  onGetFilledTemplate: () => void;
  onFileReset: () => void;
  formInstance: IFormInstance;
  selectedFile?: File;
  isTemplateLoading?: boolean;
  templateProgress?: ITemplateExportProgress | null;
  canManage: boolean;
};

const StocksImport = ({
  onSubmit,
  onCategoriesSearch,
  onGetFilledTemplate,
  categoryOptions,
  partnersOptions,
  onPartnerSearch,
  onFileReset,
  selectedFile,
  formInstance,
  onValuesChange,
  isTemplateLoading,
  templateProgress,
  canManage,
}: TPreparingStepContainer) => {
  const { t } = useTranslation(['products', 'validations']);
  const rules = useImportProductsFormRules();

  const progressTotal = templateProgress?.total ?? 0;
  const progressPercent =
    progressTotal > 0 ? Math.min(100, Math.round(((templateProgress?.progress ?? 0) / progressTotal) * 100)) : 0;

  return (
    <StyledStockWrapper>
      <StyledImportForm form={formInstance} onFinish={onSubmit} onValuesChange={onValuesChange}>
        <StyledInputWrap>
          <StyledInputWrap>
            <StyledRow>
              <Circle>1</Circle>
              <StyledLabel>{t('products_import_step', { step: 1 })}</StyledLabel>
            </StyledRow>

            <StyledSelectWrap>
              <StyledDescription>{t('stocks_import_category_label')}</StyledDescription>
              <StyledFormItem name={ImportStocksFieldNames.CATEGORY_ID}>
                <StyledSelect allowClear showSearch options={categoryOptions} onSearch={onCategoriesSearch} />
              </StyledFormItem>
              <StyledDescription>{t('stocks_import_category_description')}</StyledDescription>
            </StyledSelectWrap>

            <StyledSelectWrap>
              <StyledDescription>{t('products_import_partner_label')}</StyledDescription>
              <StyledFormItem name={ImportStocksFieldNames.PARTNER_ID} rules={rules[ImportStocksFieldNames.PARTNER_ID]}>
                <StyledSelect allowClear showSearch options={partnersOptions ?? []} onSearch={onPartnerSearch} />
              </StyledFormItem>
              <StyledDescription>{t('products_import_partner_description')}</StyledDescription>
            </StyledSelectWrap>
          </StyledInputWrap>

          <StyledInputWrap>
            <StyledRow>
              <Circle>2</Circle>
              <StyledLabel>{t('products_import_step', { step: 2 })}</StyledLabel>
              <StyledLabel $bold>{t('products_import_file_prepare')}</StyledLabel>
            </StyledRow>
            <StyledButtonBlocksWrap>
              <StyledLabel>{t('stocks_import_template_export_label')}</StyledLabel>
              <StyledButton
                onClick={onGetFilledTemplate}
                loading={isTemplateLoading}
                disabled={isTemplateLoading || !canManage}
              >
                {t('products_import_export')}
              </StyledButton>
            </StyledButtonBlocksWrap>
          </StyledInputWrap>
          {isTemplateLoading && (
            <StyledExportProgress>
              <Progress percent={progressPercent} status="active" size="small" />
              <span>
                {progressTotal > 0
                  ? t('stocks_import_template_progress', {
                      progress: templateProgress?.progress ?? 0,
                      total: progressTotal,
                    })
                  : t('stocks_import_template_preparing')}
              </span>
            </StyledExportProgress>
          )}
          <StyledInstructionBlock>
            <AttentionIcon />
            <span>
              {t('stocks_import_instruction_text_label')}
              <StyledLink href={IMPORT_STOCKS_INSTRUCTION_LINK} target="_blank" rel="noreferrer">
                {t('stocks_import_instruction_label')}
              </StyledLink>
            </span>
          </StyledInstructionBlock>
          {canManage && (
            <StyledInputWrap>
              <StyledRow>
                <Circle>3</Circle>
                <StyledLabel>{t('products_import_step', { step: 3 })}</StyledLabel>
                <StyledLabel $bold>{t('products_import_file_upload')}</StyledLabel>
              </StyledRow>
              {selectedFile && (
                <StyledUploadContent>
                  <DocumentIcon />
                  {selectedFile.name}
                </StyledUploadContent>
              )}
              <StyledFormItem name={ImportStocksFieldNames.FILE} hidden={!!selectedFile}>
                <StyledDragger value={selectedFile} acceptance={'.xlsx'} />
              </StyledFormItem>
            </StyledInputWrap>
          )}
          {selectedFile && (
            <StyledContinueBlock>
              <StyledButtonsWrap>
                {canManage && (
                  <StyledButton type={'primary'} htmlType={'submit'}>
                    {t('products_import_start_import')}
                  </StyledButton>
                )}
                <StyledButton onClick={onFileReset}>
                  <ReplaceIcon />
                  {t('products_import_reset_file')}
                </StyledButton>
              </StyledButtonsWrap>
            </StyledContinueBlock>
          )}
        </StyledInputWrap>
      </StyledImportForm>

      <StyledHintBlock>
        <StyledHintTitleWrapper>
          <StyledHintIcon>
            <AttentionIcon />
          </StyledHintIcon>
          <StyledHintTitle>{t('products_hint_attention')}</StyledHintTitle>
        </StyledHintTitleWrapper>
        <StyledHintText>{t('products_hint_attention_description')}</StyledHintText>
      </StyledHintBlock>
    </StyledStockWrapper>
  );
};

export default StocksImport;
